import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, defer, finalize, map, Observable, throwError } from 'rxjs';
import { environment } from '#environments';
import {
  ApproveDigPlanRequest,
  ApproveSiteInspectionRequest,
  CancelRecCaseRequest,
  CreateRecCaseRequest,
  CreateRecFacilityCaseRequest,
  FotDto,
  RecAttachment,
  RecBusinessPartner,
  RecCaseDetail,
  RecCaseListQuery,
  RecCasePage,
  RecCloseOutData,
  RecCrewQueueRow,
  UpdateRecCrewQueueItem,
  RecDigPlan,
  RecExecutionData,
  RecFacilityAsset,
  RecLookupOption,
  RecSchedulingData,
  RecSeedingExecutionData,
  RecSeedingSchedulingData,
  RecSiteInspection,
  RecWbsEntry,
  RecWellAsset,
  RecycleCloseOutRequest,
  RejectDigPlanRequest,
  RejectSiteInspectionRequest,
  SaveRecBusinessPartnerRequest,
  SaveRecCloseOutDataRequest,
  SaveRecDigPlanRequest,
  SaveRecExecutionDataRequest,
  SaveRecFacilityAssetRequest,
  SaveRecSchedulingDataRequest,
  SaveRecSeedingExecutionDataRequest,
  SaveRecSeedingSchedulingDataRequest,
  SaveRecSiteInspectionRequest,
  SaveRecWbsEntryRequest,
  SaveRecWellAssetRequest,
  SeedingStateRule,
} from '#models';

@Injectable({
  providedIn: 'root',
})
export class ReclamationCasesApiService {
  private readonly _http = inject(HttpClient);
  private readonly _baseUrl = `${environment.apiUrl}/rec-cases`;
  private readonly _seedingStateRulesUrl = `${environment.apiUrl}/seeding-state-rules`;
  private readonly _pendingRequests = signal(0);
  private readonly _lastError = signal<unknown | null>(null);
  private readonly _lastFacilityError = signal<unknown | null>(null);

  readonly isLoading = computed(() => this._pendingRequests() > 0);
  readonly lastError = this._lastError.asReadonly();
  readonly lastFacilityError = this._lastFacilityError.asReadonly();

  getList(query?: RecCaseListQuery): Observable<RecCasePage> {
    let params = new HttpParams();
    if (query?.page != null) params = params.set('page', query.page);
    if (query?.pageSize != null) params = params.set('pageSize', query.pageSize);
    if (query?.stage) params = params.set('stage', query.stage);
    if (query?.search) params = params.set('search', query.search);
    if (query?.fot) params = params.set('fot', query.fot);
    if (query?.assetType) params = params.set('assetType', query.assetType);
    if (query?.sort) params = params.set('sort', query.sort);
    if (query?.sortDescending != null) params = params.set('sortDescending', query.sortDescending);
    return this._track(this._http.get<RecCasePage>(this._baseUrl, { params }));
  }

  getDetail(caseNumber: string): Observable<RecCaseDetail> {
    return this._track(this._http.get<RecCaseDetail>(`${this._baseUrl}/${caseNumber}`));
  }

  getAuditLog(id: number): Observable<import('#models').AuditLogEntry[]> {
    return this._track(this._http.get<import('#models').AuditLogEntry[]>(`${this._baseUrl}/${id}/audit-log`));
  }

  getCrewQueue(crew: string, seeding = false): Observable<RecCrewQueueRow[]> {
    const params = new HttpParams().set('crew', crew).set('seeding', seeding);
    return this._track(this._http.get<RecCrewQueueRow[]>(`${this._baseUrl}/crew-queue`, { params }));
  }

  /** Batch-updates QueueOrder for cases in a crew's queue after a drag-and-drop reorder. */
  updateCrewQueueOrder(items: UpdateRecCrewQueueItem[], seeding = false): Observable<void> {
    const params = new HttpParams().set('seeding', seeding);
    return this._track(this._http.put<void>(`${this._baseUrl}/crew-queue`, items, { params }));
  }

  createWellCase(request: CreateRecCaseRequest): Observable<RecCaseDetail> {
    return this._track(this._http.post<RecCaseDetail>(`${this._baseUrl}/well`, request));
  }

  createFacilityCase(request: CreateRecFacilityCaseRequest): Observable<RecCaseDetail> {
    return this._trackFacility(this._http.post<RecCaseDetail>(`${this._baseUrl}/facility`, request));
  }

  updateWellAsset(caseNumber: string, request: SaveRecWellAssetRequest): Observable<RecWellAsset> {
    return this._track(this._http.patch<RecWellAsset>(`${this._baseUrl}/${caseNumber}/well-asset`, request));
  }

  updateFacilityAsset(caseNumber: string, request: SaveRecFacilityAssetRequest): Observable<RecFacilityAsset> {
    return this._trackFacility(this._http.patch<RecFacilityAsset>(`${this._baseUrl}/${caseNumber}/facility-asset`, request));
  }

  saveDigPlan(caseNumber: string, request: SaveRecDigPlanRequest): Observable<RecDigPlan> {
    return this._track(this._http.put<RecDigPlan>(`${this._baseUrl}/${caseNumber}/dig-plan`, request));
  }

  submitDigPlan(caseNumber: string): Observable<RecCaseDetail> {
    return this._track(this._http.put<RecCaseDetail>(`${this._baseUrl}/${caseNumber}/dig-plan/submit`, {}));
  }

  approveDigPlan(caseNumber: string, request: ApproveDigPlanRequest): Observable<RecCaseDetail> {
    return this._track(this._http.put<RecCaseDetail>(`${this._baseUrl}/${caseNumber}/dig-plan/approve`, request));
  }

  rejectDigPlan(caseNumber: string, request: RejectDigPlanRequest): Observable<RecCaseDetail> {
    return this._track(this._http.put<RecCaseDetail>(`${this._baseUrl}/${caseNumber}/dig-plan/reject`, request));
  }

  addBusinessPartner(caseNumber: string, request: SaveRecBusinessPartnerRequest): Observable<RecBusinessPartner> {
    return this._track(this._http.post<RecBusinessPartner>(`${this._baseUrl}/${caseNumber}/business-partners`, request));
  }

  updateBusinessPartner(caseNumber: string, rowId: number, request: SaveRecBusinessPartnerRequest): Observable<RecBusinessPartner> {
    return this._track(this._http.put<RecBusinessPartner>(`${this._baseUrl}/${caseNumber}/business-partners/${rowId}`, request));
  }

  deleteBusinessPartner(caseNumber: string, rowId: number): Observable<void> {
    return this._track(this._http.delete<void>(`${this._baseUrl}/${caseNumber}/business-partners/${rowId}`));
  }

  saveScheduling(caseNumber: string, request: SaveRecSchedulingDataRequest): Observable<RecSchedulingData> {
    return this._track(this._http.put<RecSchedulingData>(`${this._baseUrl}/${caseNumber}/scheduling`, request));
  }

  submitScheduling(caseNumber: string): Observable<RecCaseDetail> {
    return this._track(this._http.put<RecCaseDetail>(`${this._baseUrl}/${caseNumber}/scheduling/submit`, {}));
  }

  saveSeedingScheduling(caseNumber: string, request: SaveRecSeedingSchedulingDataRequest): Observable<RecSeedingSchedulingData> {
    return this._track(this._http.put<RecSeedingSchedulingData>(`${this._baseUrl}/${caseNumber}/seeding-scheduling`, request));
  }

  submitSeedingScheduling(caseNumber: string): Observable<RecCaseDetail> {
    return this._track(this._http.put<RecCaseDetail>(`${this._baseUrl}/${caseNumber}/seeding-scheduling/submit`, {}));
  }

  saveExecution(caseNumber: string, request: SaveRecExecutionDataRequest): Observable<RecExecutionData> {
    return this._track(this._http.put<RecExecutionData>(`${this._baseUrl}/${caseNumber}/execution`, request));
  }

  sendExecutionToSeeding(caseNumber: string): Observable<RecCaseDetail> {
    return this._track(this._http.put<RecCaseDetail>(`${this._baseUrl}/${caseNumber}/execution/send-to-seeding`, {}));
  }

  sendExecutionToSiteInspection(caseNumber: string): Observable<RecCaseDetail> {
    return this._track(this._http.put<RecCaseDetail>(`${this._baseUrl}/${caseNumber}/execution/send-to-site-inspection`, {}));
  }

  sendExecutionToCloseOut(caseNumber: string): Observable<RecCaseDetail> {
    return this._track(this._http.put<RecCaseDetail>(`${this._baseUrl}/${caseNumber}/execution/send-to-close-out`, {}));
  }

  addWbsEntry(caseNumber: string, request: SaveRecWbsEntryRequest): Observable<RecWbsEntry> {
    return this._track(this._http.post<RecWbsEntry>(`${this._baseUrl}/${caseNumber}/wbs-entries`, request));
  }

  updateWbsEntry(caseNumber: string, rowId: number, request: SaveRecWbsEntryRequest): Observable<RecWbsEntry> {
    return this._track(this._http.put<RecWbsEntry>(`${this._baseUrl}/${caseNumber}/wbs-entries/${rowId}`, request));
  }

  deleteWbsEntry(caseNumber: string, rowId: number): Observable<void> {
    return this._track(this._http.delete<void>(`${this._baseUrl}/${caseNumber}/wbs-entries/${rowId}`));
  }

  saveSeedingExecution(caseNumber: string, request: SaveRecSeedingExecutionDataRequest): Observable<RecSeedingExecutionData> {
    return this._track(this._http.put<RecSeedingExecutionData>(`${this._baseUrl}/${caseNumber}/seeding-execution`, request));
  }

  completeSeedingExecution(caseNumber: string): Observable<RecCaseDetail> {
    return this._track(this._http.put<RecCaseDetail>(`${this._baseUrl}/${caseNumber}/seeding-execution/complete`, {}));
  }

  saveSiteInspection(caseNumber: string, request: SaveRecSiteInspectionRequest): Observable<RecSiteInspection> {
    return this._track(this._http.put<RecSiteInspection>(`${this._baseUrl}/${caseNumber}/site-inspection`, request));
  }

  approveSiteInspection(caseNumber: string, request: ApproveSiteInspectionRequest): Observable<RecCaseDetail> {
    return this._track(this._http.put<RecCaseDetail>(`${this._baseUrl}/${caseNumber}/site-inspection/approve`, request));
  }

  rejectSiteInspection(caseNumber: string, request: RejectSiteInspectionRequest): Observable<RecCaseDetail> {
    return this._track(this._http.put<RecCaseDetail>(`${this._baseUrl}/${caseNumber}/site-inspection/reject`, request));
  }

  saveCloseOut(caseNumber: string, request: SaveRecCloseOutDataRequest): Observable<RecCloseOutData> {
    return this._track(this._http.put<RecCloseOutData>(`${this._baseUrl}/${caseNumber}/close-out`, request));
  }

  completeCloseOut(caseNumber: string): Observable<RecCaseDetail> {
    return this._track(this._http.put<RecCaseDetail>(`${this._baseUrl}/${caseNumber}/close-out/complete`, {}));
  }

  recycleCloseOut(caseNumber: string, request: RecycleCloseOutRequest): Observable<RecCaseDetail> {
    return this._track(this._http.put<RecCaseDetail>(`${this._baseUrl}/${caseNumber}/close-out/recycle`, request));
  }

  cancel(caseNumber: string, request: CancelRecCaseRequest): Observable<RecCaseDetail> {
    return this._track(this._http.put<RecCaseDetail>(`${this._baseUrl}/${caseNumber}/cancel`, request));
  }

  rework(caseNumber: string, request: { moveToStage: string; comments: string }): Observable<RecCaseDetail> {
    return this._track(this._http.put<RecCaseDetail>(`${this._baseUrl}/${caseNumber}/rework`, request));
  }

  getAttachments(id: number): Observable<RecAttachment[]> {
    return this._track(this._http.get<RecAttachment[]>(`${this._baseUrl}/${id}/attachments`));
  }

  getNotificationLog(id: number): Observable<{ sentOnUtc: string; recipients: string; subject: string }[]> {
    return this._track(this._http.get<{ sentOnUtc: string; recipients: string; subject: string }[]>(`${this._baseUrl}/${id}/notification-log`));
  }

  uploadAttachment(id: number, attachmentType: string, file: File): Observable<RecAttachment> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('attachmentType', attachmentType);

    return this._track(this._http.post<RecAttachment>(`${this._baseUrl}/${id}/attachments`, formData));
  }

  deleteAttachment(id: number, attachmentId: number): Observable<void> {
    return this._track(this._http.delete<void>(`${this._baseUrl}/${id}/attachments/${attachmentId}`));
  }

  getAttachmentDownloadUrl(id: number, attachmentId: number): Observable<{ downloadUrl: string }> {
    return this._track(this._http.get<{ downloadUrl: string }>(`${this._baseUrl}/${id}/attachments/${attachmentId}/download`));
  }

  updateJobPlanAttachment(caseNumber: string, reclamationJobPlanAttachmentId: number | null): Observable<RecCaseDetail> {
    return this._track(this._http.put<RecCaseDetail>(`${this._baseUrl}/${caseNumber}/job-plan-attachment`, { reclamationJobPlanAttachmentId }));
  }

  getSeedingStateRules(): Observable<SeedingStateRule[]> {
    return this._track(this._http.get<SeedingStateRule[]>(this._seedingStateRulesUrl));
  }

  getSeedingMethodOptions(): Observable<RecLookupOption[]> {
    return this._track(this._http.get<RecLookupOption[]>(`${this._baseUrl}/reference-data/REC.SeedingMethod`));
  }

  getSiteReviewApprovalResultOptions(): Observable<RecLookupOption[]> {
    return this._track(this._http.get<RecLookupOption[]>(`${this._baseUrl}/reference-data/REC.SiteReviewApprovalResults`));
  }

  getDigPlanApprovalStatusOptions(): Observable<RecLookupOption[]> {
    return this._track(this._http.get<RecLookupOption[]>(`${this._baseUrl}/reference-data/REC.DigPlanApprovalStatus`));
  }

  getMoveToStageOptions(): Observable<RecLookupOption[]> {
    return this._track(this._http.get<RecLookupOption[]>(`${this._baseUrl}/reference-data/REC.MoveToStage`));
  }

  getCrewTypeOptions(): Observable<RecLookupOption[]> {
    return this._track(this._http.get<RecLookupOption[]>(`${this._baseUrl}/reference-data/REC.CrewType`));
  }

  getFotOptions(): Observable<RecLookupOption[]> {
    return this._track(this._http.get<FotDto[]>(`${environment.apiUrl}/fots`).pipe(
      map((fots) => fots.map((fot) => ({ id: fot.id, value: fot.name, label: fot.name }))),
    ));
  }

  getAreaOptions(): Observable<RecLookupOption[]> {
    return this._track(this._http.get<string[]>(`${environment.apiUrl}/well-info/areas`).pipe(
      map((areas) => areas.map((area) => ({ value: area, label: area }))),
    ));
  }

  getStateOptions(): Observable<RecLookupOption[]> {
    return this._track(this._http.get<string[]>(`${environment.apiUrl}/well-info/states`).pipe(
      map((states) => states.map((state) => ({ value: state, label: state }))),
    ));
  }

  private _track<T>(request$: Observable<T>): Observable<T> {
    return defer(() => {
      this._pendingRequests.update((count) => count + 1);
      this._lastError.set(null);

      return request$.pipe(
        catchError((error: unknown) => {
          this._lastError.set(error);
          return throwError(() => error);
        }),
        finalize(() => this._pendingRequests.update((count) => Math.max(0, count - 1))),
      );
    });
  }

  private _trackFacility<T>(request$: Observable<T>): Observable<T> {
    return defer(() => {
      this._lastFacilityError.set(null);

      return this._track(request$).pipe(
        catchError((error: unknown) => {
          this._lastFacilityError.set(error);
          return throwError(() => error);
        }),
      );
    });
  }
}
